// components/issues/IssueReportPanel.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import {
  Button,
  Box,
  Typography,
  Grid,
  IconButton,
  CircularProgress,
  TextField,
  Popover,
  Badge,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  InputAdornment,
  useMediaQuery,
  Tabs,
  Tab,
} from "@mui/material";

import { toast } from "react-toastify";
import axios from "axios";
import fileDownload from "js-file-download";
import Moment from "moment";
import { FileUploader } from "react-drag-drop-files";

import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CachedOutlinedIcon from "@mui/icons-material/CachedOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import UploadCloudIcon from "@mui/icons-material/CloudUpload";
import SendIcon from "@mui/icons-material/Send";
import ImageIcon from "@mui/icons-material/Image";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import LifeBuoyIcon from "@mui/icons-material/SupportAgent";
import InboxIcon from "@mui/icons-material/Inbox";
import MessageIcon from "@mui/icons-material/Message";
import CalendarIcon from "@mui/icons-material/CalendarToday";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { MessageTwoTone, CheckCircleOutline, CheckCircle } from "@mui/icons-material";

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const PATIENT_ID = -7;

const getFileIcon = (name, fontSize = "small") => {
  if (!name) return <InsertDriveFileIcon fontSize={fontSize} />;
  const ext = name.split(".").pop()?.toLowerCase();
  if (["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(ext || ""))
    return <ImageIcon fontSize={fontSize} />;
  if (["mp4", "mov", "avi", "webm", "mkv"].includes(ext || ""))
    return <VideoFileIcon fontSize={fontSize} />;
  if (ext === "pdf") return <PictureAsPdfIcon fontSize={fontSize} />;
  return <InsertDriveFileIcon fontSize={fontSize} />;
};

// Helper: check if resolved (view: true)
const isResolved = (doc) => {
  return doc.view === true || doc.view === "true" || doc.view === 1;
};

const IssueReportPanel = (props) => {
  const { t } = props;

  const token =
    typeof window !== "undefined" ? localStorage.getItem("Token") : null;
  const headers = { Authorization: `Bearer ${token}` };
  const API_BASE = process.env.REACT_APP_API_URL;

  const [anchorEl, setAnchorEl] = useState(null);
  const isOpen = Boolean(anchorEl);

  const [docslist, setDocslist] = useState([]);
  const [spinner, setSpinner] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // 0 = Pending, 1 = Resolved

  // Add dialog state
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [date, setDate] = useState(Moment().format("YYYY-MM-DD"));
  const [comment, setComment] = useState("");
  const [filename, setFilename] = useState([]);
  const [base64Data, setBase64Data] = useState([]);
  const [commentError, setCommentError] = useState(false);
  const [sizeError, setSizeError] = useState(false);

  // Search + preview
  const [searchQuery, setSearchQuery] = useState("");
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [urlFile, seturlFile] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);

  // Delete confirm
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [delById, setDelById] = useState(null);

  // Resolve confirm
  const [resolveOpen, setResolveOpen] = useState(false);
  const [resolveDoc, setResolveDoc] = useState(null);
  const [resolving, setResolving] = useState(false);

  const anyDialogOpen = open || previewOpen || deleteOpen || resolveOpen;

  // ─── Fetch ──────────────────────────────────────────
  const fetchDocs = useCallback(async () => {
    setSpinner(true);
    try {
      const res = await axios.get(
        `${API_BASE}/PatientDocument/PatientDocumentList?patientId=${PATIENT_ID}`,
        { headers }
      );
      setDocslist(res.data || res || []);
    } catch (err) {
      console.error("Error fetching reports:", err);
      toast.error("Failed to load reports");
    } finally {
      setSpinner(false);
    }
  }, [API_BASE, token]);

  useEffect(() => {
    // if (!isOpen) return;
    fetchDocs();
  }, [isOpen, fetchDocs]);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleCloseDrop = () => {
    if (anyDialogOpen) return;
    setAnchorEl(null);
  };
  const applyRefresh = () => {
    setIsRefreshing(true);
    fetchDocs().finally(() => setTimeout(() => setIsRefreshing(false), 500));
  };
  const isTabletOrMobile = useMediaQuery("(max-width:768px)");

  const resetForm = () => {
    setOpen(false);
    setDate(Moment().format("YYYY-MM-DD"));
    setComment("");
    setFilename([]);
    setBase64Data([]);
    setCommentError(false);
    setSizeError(false);
    setSubmitting(false);
  };

  // ─── File Handling ──────────────────────────────────
  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (err) => reject(err);
    });

  const processFiles = async (files) => {
    const arr = Array.isArray(files) ? files : Array.from(files || []);
    const names = [];
    const b64 = [];
    for (const f of arr) {
      if (f.size > MAX_FILE_SIZE) {
        setSizeError(true);
        toast.error(`"${f.name}" exceeds 10 MB limit`);
        return;
      }
      names.push(f.name);
      b64.push(await fileToBase64(f));
    }
    setFilename((prev) => [...prev, ...names]);
    setBase64Data((prev) => [...prev, ...b64]);
    setSizeError(false);
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf("image") !== -1) {
        const blob = items[i].getAsFile();
        if (blob) {
          files.push(
            new File([blob], `pasted-image-${Date.now()}.png`, {
              type: blob.type || "image/png",
            })
          );
        }
      }
    }
    if (files.length > 0) {
      processFiles(files);
      toast.success("Image pasted from clipboard!");
      e.preventDefault();
    }
  };

  const removeSelectedFile = (index) => {
    setFilename((prev) => prev.filter((_, i) => i !== index));
    setBase64Data((prev) => prev.filter((_, i) => i !== index));
  };

  // ─── Submit ─────────────────────────────────────────
  const handleSubmit = async () => {
    if (!comment.trim()) {
      setCommentError(true);
      toast.error("Please describe your issue.");
      return;
    }
    setCommentError(false);
    setSubmitting(true);

    try {
      const postData = base64Data.map((b64, i) => ({
        patientId: PATIENT_ID,
        date,
        documentName: filename[i] || "",
        base64: b64,
        linkText: "",
        linkUrl: "",
        comments: comment.trim(),
        view: false, // Default pending
      }));

      if (postData.length === 0) {
        postData.push({
          patientId: PATIENT_ID,
          date,
          documentName: "",
          base64: "",
          linkText: "",
          linkUrl: "",
          comments: comment.trim(),
          view: false,
        });
      }

      await Promise.all(
        postData.map((d) =>
          axios.post(
            `${API_BASE}/PatientDocument/PatientDocument?patientId=${PATIENT_ID}`,
            d,
            { headers: { ...headers, "Content-Type": "application/json" } }
          )
        )
      );

      toast.success("Your issue has been reported successfully!");
      resetForm();
      applyRefresh();
    } catch (err) {
      console.error("SUBMIT ERROR:", err);
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Something went wrong while reporting the issue."
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Resolve (sends full record + view: true) ───────
  const handleResolve = async () => {
    if (!resolveDoc) return;
    setResolving(true);
    try {
      const payload = {
        ...resolveDoc, // Sending all fields including id, documentName, fileUrl
        view: true, // Mark as resolved
      };

      await axios.post(
        `${API_BASE}/PatientDocument/PatientDocument?patientId=${PATIENT_ID}`,
        payload,
        { headers: { ...headers, "Content-Type": "application/json" } }
      );

      // Optimistic update
      setDocslist((prev) =>
        prev.map((d) => (d.id === resolveDoc.id ? { ...d, view: true } : d))
      );
      setPreviewDoc((prev) =>
        prev && prev.id === resolveDoc.id ? { ...prev, view: true } : prev
      );

      toast.success("Issue marked as resolved.");
      setResolveOpen(false);
      setResolveDoc(null);
      applyRefresh();
    } catch (err) {
      console.error("Resolve error:", err);
      toast.error("Failed to resolve the report.");
    } finally {
      setResolving(false);
    }
  };

  // ─── Delete ─────────────────────────────────────────
  const handleDelete = async () => {
    if (!delById) return;
    try {
      await axios.post(
        `${API_BASE}/PatientDocument/PatientDocument?patientId=${PATIENT_ID}`,
        { id: delById, deleted: true },
        { headers }
      );
      toast.success("Report deleted successfully.");
      setDeleteOpen(false);
      setDelById(null);
      applyRefresh();
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("Failed to delete the report.");
    }
  };

  // ─── Download ───────────────────────────────────────
  const download = (url, name) => {
    axios
      .get(`${API_BASE}/PatientDocument/DownloadAWSFile?path=${url}`, {
        headers,
        responseType: "blob",
      })
      .then((res) => {
        fileDownload(res.data, name);
        toast.success("Attachment downloaded.");
      })
      .catch((err) => {
        console.error(err);
        toast.error("Download failed.");
      });
  };

  // ─── Preview ────────────────────────────────────────
  const handlePreviewDocument = (doc) => {
    setPreviewDoc(doc);
    seturlFile("");
    setPreviewOpen(true);
    if (doc.fileUrl) {
      setPreviewLoading(true);
      axios
        .get(
          `${API_BASE}/PatientDocument/ViewPatientDocument?path=${doc.fileUrl}`,
          { headers }
        )
        .then((r) => seturlFile(r))
        .catch((e) => console.error("Preview error:", e))
        .finally(() => setPreviewLoading(false));
    }
  };

  // ─── Filtering & Tabs ───────────────────────────────
  const pendingDocs = docslist.filter((d) => !isResolved(d));
  const resolvedDocs = docslist.filter((d) => isResolved(d));

  const pendingCount = pendingDocs.length;
  const resolvedCount = resolvedDocs.length;
  const totalCount = docslist.length;

  const baseList = activeTab === 0 ? pendingDocs : resolvedDocs;

  const filteredDocs = baseList
    .filter((doc) => {
      const q = (searchQuery || "").toLowerCase();
      return (
        (doc.comments || "").toLowerCase().includes(q) ||
        (doc.documentName || "").toLowerCase().includes(q) ||
        (doc.createdBy || "").toLowerCase().includes(q)
      );
    })
    .sort((a, b) => (a.id < b.id ? 1 : -1));

  const canSubmit = comment.trim().length > 0;

  // ─── Card ───────────────────────────────────────────
  const renderCard = (doc) => {
    const hasFile = !!doc.fileUrl;
    const resolved = isResolved(doc);
    const formattedDate = doc.date
      ? Moment(doc.date).format("DD-MM-YYYY")
      : "N/A";

    return (
      <Box
        key={doc.id}
        onClick={() => handlePreviewDocument(doc)}
        sx={{
          cursor: "pointer",
          borderRadius: 2,
          border: "1px solid",
          borderColor: resolved ? "#e5e7eb" : "#e5e7eb",
          bgcolor: resolved ? "#f8fafc" : "#fff",
          p: 1.5,
          mb: 1.25,
          transition: "all .2s",
          "&:hover": {
            borderColor: resolved ? "#22c55e" : "#377562",
            boxShadow: resolved
              ? "0 2px 8px rgba(34,197,94,0.12)"
              : "0 2px 8px rgba(55,117,98,0.12)",
          },
        }}
      >
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              flexShrink: 0,
              borderRadius: 1.5,
              bgcolor: resolved ? "rgba(34,197,94,0.1)" : "rgba(55,117,98,0.1)",
              color: resolved ? "#16a34a" : "#377562",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {hasFile ? (
              getFileIcon(doc.documentName)
            ) : (
              <MessageIcon fontSize="small" />
            )}
          </Box>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#111827",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  minWidth: 0,
                }}
              >
                {doc.documentName || "Issue Report"}
              </Typography>
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: "#9ca3af",
                  flexShrink: 0,
                }}
              >
                {formattedDate}
              </Typography>
            </Box>

            <Typography
              sx={{
                mt: 0.5,
                fontSize: 11.5,
                lineHeight: 1.5,
                color: "#4b5563",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {doc.comments || "No description provided."}
            </Typography>

            <Box
              sx={{
                mt: 1,
                pt: 1,
                borderTop: "1px solid #f3f4f6",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    bgcolor: resolved ? "#dcfce7" : "#e5e7eb",
                    color: resolved ? "#16a34a" : "#4b5563",
                    fontSize: 9,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {(doc.createdBy?.[0] || "U").toUpperCase()}
                </Box>
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 500,
                    color: "#6b7280",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {doc.createdBy || ""}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                {/* Resolve Button */}
                {!resolved && (
                  <Tooltip title="Mark as Resolved">
                    <Button
                      size="small"
                      variant="contained"
                      onClick={(e) => {
                        e.stopPropagation();
                        setResolveDoc(doc);
                        setResolveOpen(true);
                      }}
                      sx={{
                        minWidth: 0,
                        px: 1.5,
                        py: 0.25,
                        height: 24,
                        bgcolor: "#22c55e",
                        color: "#fff",
                        fontSize: 9,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        borderRadius: 1,
                        "&:hover": { bgcolor: "#16a34a" },
                      }}
                      startIcon={
                        <CheckCircleOutline sx={{ fontSize: "12px !important" }} />
                      }
                    >
                      Resolve
                    </Button>
                  </Tooltip>
                )}

                {resolved && (
                  <Chip
                    icon={<CheckCircle sx={{ fontSize: "12px !important" }} />}
                    label="RESOLVED"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: 9,
                      fontWeight: 700,
                      bgcolor: "#dcfce7",
                      color: "#16a34a",
                      borderRadius: 1,
                    }}
                  />
                )}

                {hasFile && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      download(doc.fileUrl, doc.documentName);
                    }}
                    sx={{
                      width: 24,
                      height: 24,
                      bgcolor: "#eff6ff",
                      color: "#2563eb",
                      borderRadius: 1,
                      "&:hover": { bgcolor: "#dbeafe" },
                    }}
                  >
                    <DownloadIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                )}
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDelById(doc.id);
                    setDeleteOpen(true);
                  }}
                  sx={{
                    width: 24,
                    height: 24,
                    bgcolor: "#fef2f2",
                    color: "#dc2626",
                    borderRadius: 1,
                    "&:hover": { bgcolor: "#fee2e2" },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  // ─── UI ─────────────────────────────────────────────
  return (
    <React.Fragment>
      {/* Trigger Button */}
     <div className="d-flex">
  <Grid
    style={{
      display: "flex",
      marginTop: "-15px",
    }}
  >
    <Badge
      badgeContent={pendingCount > 99 ? "99+" : pendingCount}
      color="error"
      overlap="circular"
      invisible={pendingCount === 0}
      sx={{
        "& .MuiBadge-badge": {
          fontSize: "0.7rem",
          height: 18,
          minWidth: 18,
          top: 20, // 👈 Adjusts vertically to sit nicely on the button
          right: 8, // 👈 Adjusts horizontally
          boxShadow: "0 0 0 2px #fff", // Gives a clean cutout effect
        },
      }}
    >
      <Button
        variant="contained"
        onClick={handleClick}
        sx={{
          backgroundColor: "#0d7f6a",
          color: "white",
          borderRadius: "30px",
          textTransform: "none",
          marginTop: "19px",
          minWidth: isTabletOrMobile ? "40px" : "150px",
          height: isTabletOrMobile ? "40px" : "38px",
          padding: isTabletOrMobile ? "0" : "0 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "&:hover": {
            background: "#2c5e4e",
          },
        }}
        startIcon={<LifeBuoyIcon sx={{ fontSize: 20 }} />}
      >
        {!isTabletOrMobile && " Report Issue"}
      </Button>
    </Badge>
  </Grid>
</div>
      {/* Popover */}
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleCloseDrop}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          style: {
            width: "min(460px, calc(100vw - 2rem))",
            borderRadius: 12,
            boxShadow: "0px 8px 30px rgba(0,0,0,0.15)",
            overflow: "hidden",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.75,
            bgcolor: "#f9fafb",
            borderBottom: "1px solid #f3f4f6",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LifeBuoyIcon sx={{ fontSize: 20, color: "#377562" }} />
            <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#111827" }}>
              Issue Center
            </Typography>
            <Chip
              label={totalCount}
              size="small"
              sx={{
                bgcolor: "rgba(55,117,98,0.1)",
                color: "#377562",
                fontWeight: 600,
                height: 20,
                fontSize: 11,
              }}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Tooltip title="Refresh">
              <span>
                <IconButton
                  size="small"
                  onClick={applyRefresh}
                  disabled={isRefreshing}
                  sx={{ color: "#6b7280" }}
                >
                  {isRefreshing ? (
                    <CircularProgress size={16} />
                  ) : (
                    <CachedOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <Button
              size="small"
              variant="contained"
              onClick={() => setOpen(true)}
              startIcon={<AddOutlinedIcon fontSize="small" />}
              sx={{
                bgcolor: "#377562",
                color: "#fff",
                borderRadius: 20,
                textTransform: "none",
                fontSize: 12,
                fontWeight: 600,
                height: 30,
                px: 1.5,
                "&:hover": { bgcolor: "#2c5e4e" },
              }}
            >
              New
            </Button>
          </Box>
        </Box>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={(e, val) => setActiveTab(val)}
          variant="fullWidth"
          sx={{
            borderBottom: "1px solid #f3f4f6",
            minHeight: 36,
            "& .MuiTab-root": {
              textTransform: "none",
              fontSize: 12,
              fontWeight: 600,
              minHeight: 36,
              py: 0,
            },
            "& .Mui-selected": {
              color: activeTab === 0 ? "#377562 !important" : "#16a34a !important",
            },
            "& .MuiTabs-indicator": {
              backgroundColor: activeTab === 0 ? "#377562" : "#16a34a",
            },
          }}
        >
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                Pending
                {pendingCount > 0 && (
                  <Chip
                    label={pendingCount}
                    size="small"
                    sx={{
                      height: 16,
                      fontSize: 9,
                      fontWeight: 700,
                      bgcolor: "#ef4444",
                      color: "#fff",
                    }}
                  />
                )}
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                Resolved
                {resolvedCount > 0 && (
                  <Chip
                    label={resolvedCount}
                    size="small"
                    sx={{
                      height: 16,
                      fontSize: 9,
                      fontWeight: 700,
                      bgcolor: "#22c55e",
                      color: "#fff",
                    }}
                  />
                )}
              </Box>
            }
          />
        </Tabs>

        {/* Search */}
        <Box sx={{ p: 1.5, borderBottom: "1px solid #f3f4f6" }}>
          <TextField
            fullWidth
            size="small"
            placeholder={`Search ${activeTab === 0 ? "pending" : "resolved"} reports...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: "#f9fafb",
                borderRadius: 2,
                fontSize: 13,
              },
            }}
          />
        </Box>

        {/* List */}
        <Box
          sx={{
            maxHeight: 380,
            overflowY: "auto",
            p: 1.5,
            bgcolor: "#fafafa",
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "#d1d5db",
              borderRadius: 3,
            },
          }}
        >
          {spinner ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : filteredDocs.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6, px: 2 }}>
              <Box
                sx={{
                  display: "inline-flex",
                  p: 1.5,
                  borderRadius: "50%",
                  bgcolor: "#f3f4f6",
                  mb: 1.5,
                }}
              >
                {searchQuery ? (
                  <SearchIcon sx={{ fontSize: 28, color: "#9ca3af" }} />
                ) : activeTab === 0 ? (
                  <InboxIcon sx={{ fontSize: 28, color: "#9ca3af" }} />
                ) : (
                  <CheckCircle sx={{ fontSize: 28, color: "#9ca3af" }} />
                )}
              </Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
                {searchQuery
                  ? "No matching reports"
                  : activeTab === 0
                  ? "No pending reports"
                  : "No resolved reports"}
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#6b7280", mt: 0.5 }}>
                {searchQuery
                  ? "Try a different search term"
                  : activeTab === 0
                  ? "Click '+' to report your first issue"
                  : "Resolved issues will appear here"}
              </Typography>
            </Box>
          ) : (
            filteredDocs.map(renderCard)
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            textAlign: "center",
            py: 1.25,
            borderTop: "1px solid #f3f4f6",
            bgcolor: "#fff",
          }}
        >
          <Typography sx={{ fontSize: 11, color: "#9ca3af" }}>
            {totalCount} reports · {pendingCount} pending · {resolvedCount} resolved
          </Typography>
        </Box>
      </Popover>

      {/* ─── Add Dialog ─── */}
      <Dialog
        open={open}
        onClose={resetForm}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          onPaste={handlePaste}
          sx={{
            bgcolor: "#f9fafb",
            borderBottom: "1px solid #f3f4f6",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            py: 1.75,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              bgcolor: "rgba(55,117,98,0.1)",
              color: "#377562",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <MessageTwoTone fontSize="small" />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>
              Report an Issue
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#6b7280" }}>
              Tell us what's wrong — we're listening.
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent
          dividers
          onPaste={handlePaste}
          sx={{ maxHeight: "65vh", overflowY: "auto" }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Date */}
            <Box>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#374151",
                  mb: 0.75,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                }}
              >
                <CalendarIcon sx={{ fontSize: 14, color: "#377562" }} />
                Date
              </Typography>
              <TextField
                type="date"
                fullWidth
                size="small"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </Box>

            {/* Description */}
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 0.75,
                }}
              >
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                  Describe your issue <span style={{ color: "#ef4444" }}>*</span>
                </Typography>
                <Typography
                  sx={{ fontSize: 10, color: commentError ? "#ef4444" : "#9ca3af" }}
                >
                  {comment.length}/1000
                </Typography>
              </Box>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={comment}
                onChange={(e) => {
                  setComment(e.target.value.slice(0, 1000));
                  if (commentError) setCommentError(false);
                }}
                placeholder="Explain what happened, what you expected, and any steps to reproduce..."
                error={commentError}
                helperText={commentError ? "Please describe your issue." : ""}
                FormHelperTextProps={{ sx: { fontSize: 11, m: 0, mt: 0.5 } }}
              />
            </Box>

            {/* Attachments */}
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 0.75,
                }}
              >
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                  Screenshots / Files{" "}
                  <span style={{ color: "#9ca3af", fontWeight: 400 }}>
                    (Optional)
                  </span>
                </Typography>
                <Typography sx={{ fontSize: 10, color: "#9ca3af" }}>
                  Max 10 MB each
                </Typography>
              </Box>

              <Box
                sx={{
                  border: "2px dashed",
                  borderColor: sizeError ? "#f87171" : "#e5e7eb",
                  bgcolor: sizeError ? "#fef2f2" : "#f9fafb",
                  borderRadius: 2,
                  px: 2,
                  py: 2.5,
                  textAlign: "center",
                  transition: "all .2s",
                  "&:hover": { borderColor: "#377562", bgcolor: "rgba(55,117,98,0.03)" },
                }}
              >
                <FileUploader
                  handleChange={(files) => processFiles(files)}
                  name="file"
                  types={[
                    "PDF", "DOCX", "DOC", "TXT",
                    "JPG", "JPEG", "PNG", "GIF", "WEBP",
                    "MP4", "MOV", "WEBM",
                  ]}
                  multiple
                >
                  <Box>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1.5,
                        bgcolor: "rgba(55,117,98,0.1)",
                        color: "#377562",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 1,
                      }}
                    >
                      <UploadCloudIcon />
                    </Box>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                      Drop files, click to browse, or{" "}
                      <span style={{ color: "#377562" }}>Paste (Ctrl+V)</span>
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: "#9ca3af", mt: 0.25 }}>
                      Images, videos, PDFs & docs — up to 10 MB
                    </Typography>
                  </Box>
                </FileUploader>
              </Box>

              {sizeError && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mt: 0.75,
                    color: "#ef4444",
                  }}
                >
                  <ErrorOutlineIcon sx={{ fontSize: 14 }} />
                  <Typography sx={{ fontSize: 11 }}>
                    One or more files exceed the 10 MB limit.
                  </Typography>
                </Box>
              )}

              {filename.length > 0 && (
                <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 0.75 }}>
                  {filename.map((name, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderRadius: 1.5,
                        border: "1px solid rgba(55,117,98,0.2)",
                        bgcolor: "rgba(55,117,98,0.05)",
                        px: 1.25,
                        py: 0.75,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          minWidth: 0,
                          color: "#377562",
                        }}
                      >
                        {getFileIcon(name)}
                        <Typography
                          sx={{
                            fontSize: 12,
                            fontWeight: 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {name}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => removeSelectedFile(idx)}
                        sx={{
                          color: "#377562",
                          "&:hover": { color: "#ef4444" },
                          p: 0.25,
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2,
            py: 1.25,
            bgcolor: "#f9fafb",
            borderTop: "1px solid #f3f4f6",
          }}
        >
          <Button
            onClick={resetForm}
            disabled={submitting}
            variant="outlined"
            sx={{ textTransform: "none", borderRadius: 1.5, fontSize: 12 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !canSubmit}
            variant="contained"
            startIcon={
              submitting ? (
                <CircularProgress size={14} sx={{ color: "#fff" }} />
              ) : (
                <SendIcon sx={{ fontSize: 16 }} />
              )
            }
            sx={{
              textTransform: "none",
              borderRadius: 1.5,
              fontSize: 12,
              fontWeight: 600,
              bgcolor: "#377562",
              "&:hover": { bgcolor: "#2c5e4e" },
            }}
          >
            {submitting ? "Submitting..." : "Submit Report"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Preview Dialog ─── */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          sx={{
            bgcolor: "#f9fafb",
            borderBottom: "1px solid #f3f4f6",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            py: 1.75,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                bgcolor: "rgba(55,117,98,0.1)",
                color: "#377562",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <InsertDriveFileIcon fontSize="small" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#111827",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {previewDoc?.documentName || "Report Preview"}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.25 }}>
                <Chip
                  icon={<MessageIcon sx={{ fontSize: "12px !important" }} />}
                  label="ISSUE REPORT"
                  size="small"
                  sx={{
                    bgcolor: "rgba(55,117,98,0.1)",
                    color: "#377562",
                    fontWeight: 600,
                    height: 18,
                    fontSize: 9,
                  }}
                />
                <Typography sx={{ fontSize: 11, color: "#6b7280" }}>
                  {previewDoc?.date}
                </Typography>
                {previewDoc && isResolved(previewDoc) && (
                   <Chip
                   icon={<CheckCircle sx={{ fontSize: "12px !important" }} />}
                   label="RESOLVED"
                   size="small"
                   sx={{
                     bgcolor: "#dcfce7",
                     color: "#16a34a",
                     fontWeight: 600,
                     height: 18,
                     fontSize: 9,
                   }}
                 />
                )}
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
             {/* Resolve Button inside Preview */}
             {previewDoc && !isResolved(previewDoc) && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<CheckCircleOutline sx={{ fontSize: 14 }} />}
                  onClick={() => {
                    setResolveDoc(previewDoc);
                    setResolveOpen(true);
                  }}
                  sx={{
                    textTransform: "none",
                    borderRadius: 1.5,
                    fontSize: 11,
                    bgcolor: "#22c55e",
                    "&:hover": { bgcolor: "#16a34a" },
                  }}
                >
                  Resolve
                </Button>
             )}

            {previewDoc?.fileUrl && (
              <Button
                size="small"
                variant="contained"
                startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                onClick={() =>
                  download(previewDoc.fileUrl, previewDoc.documentName)
                }
                sx={{
                  textTransform: "none",
                  borderRadius: 1.5,
                  fontSize: 11,
                  bgcolor: "#377562",
                  "&:hover": { bgcolor: "#2c5e4e" },
                }}
              >
                Download
              </Button>
            )}
            <IconButton
              size="small"
              onClick={() => setPreviewOpen(false)}
              sx={{ color: "#6b7280" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ bgcolor: "#fafafa", p: 2 }}>
          <Grid container spacing={2}>
            {/* Description */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  bgcolor: "#fff",
                  borderRadius: 2,
                  border: "1px solid #e5e7eb",
                  p: 2,
                  height: "100%",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 1,
                    textTransform: "uppercase",
                    color: "#9ca3af",
                    mb: 1,
                  }}
                >
                  Description
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      bgcolor: "#377562",
                      color: "#fff",
                      fontSize: 11,
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {(previewDoc?.createdBy?.[0] || "U").toUpperCase()}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#111827",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {previewDoc?.createdBy?.split("@")[0] || "Unknown"}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: "#9ca3af" }}>
                      {previewDoc?.createdDate || previewDoc?.date}
                    </Typography>
                  </Box>
                </Box>
                <Typography
                  sx={{
                    fontSize: 12,
                    lineHeight: 1.6,
                    color: "#4b5563",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {previewDoc?.comments || "No description provided."}
                </Typography>
                {previewDoc?.linkUrl && (
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                    onClick={() => window.open(previewDoc.linkUrl, "_blank")}
                    sx={{
                      mt: 1.5,
                      textTransform: "none",
                      borderRadius: 1.5,
                      fontSize: 11,
                      borderColor: "#377562",
                      color: "#377562",
                    }}
                  >
                    Open Linked Resource
                  </Button>
                )}
              </Box>
            </Grid>

            {/* Preview area */}
            <Grid item xs={12} md={8}>
              <Box
                sx={{
                  bgcolor: "#fff",
                  borderRadius: 2,
                  border: "1px solid #e5e7eb",
                  minHeight: 400,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                {previewLoading ? (
                  <CircularProgress size={32} sx={{ color: "#377562" }} />
                ) : urlFile ? (
                  (() => {
                    const fileName = (previewDoc?.documentName || "").toLowerCase();
                    const ext = fileName.split(".").pop();
                    const imageExts = ["png", "jpg", "jpeg", "gif", "webp", "svg"];
                    const videoExts = ["mp4", "mov", "avi", "webm", "mkv"];

                    if (imageExts.includes(ext)) {
                      return (
                        <Box
                          component="img"
                          src={urlFile}
                          alt={previewDoc?.documentName || "Preview"}
                          sx={{
                            width: "100%",
                            height: "55vh",
                            objectFit: "contain",
                            display: "block",
                            bgcolor: "#f9fafb",
                          }}
                        />
                      );
                    }

                    if (videoExts.includes(ext)) {
                      return (
                        <Box
                          component="video"
                          src={urlFile}
                          controls
                          sx={{
                            width: "100%",
                            height: "55vh",
                            bgcolor: "#000",
                            outline: "none",
                          }}
                        />
                      );
                    }

                    return (
                      <Box
                        component="iframe"
                        src={urlFile}
                        title="Report"
                        sx={{ width: "100%", height: "55vh", border: 0 }}
                      />
                    );
                  })()
                ) : (
                  <Box sx={{ textAlign: "center", p: 3 }}>
                    <InsertDriveFileIcon
                      sx={{ fontSize: 40, color: "#9ca3af", mb: 1 }}
                    />
                    <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#6b7280" }}>
                      No preview available
                    </Typography>
                    {previewDoc?.fileUrl && (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                        onClick={() => download(previewDoc.fileUrl, previewDoc.documentName)}
                        sx={{
                          mt: 1.5,
                          textTransform: "none",
                          borderRadius: 1.5,
                          fontSize: 11,
                          bgcolor: "#377562",
                          "&:hover": { bgcolor: "#2c5e4e" },
                        }}
                      >
                        Download instead
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2,
            py: 1.25,
            bgcolor: "#fff",
            borderTop: "1px solid #f3f4f6",
            justifyContent: "space-between",
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              color: "#6b7280",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {previewDoc?.documentName || "Text Report"}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setPreviewOpen(false)}
            sx={{ textTransform: "none", borderRadius: 1.5, fontSize: 11 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Confirm Delete ─── */}
      <Dialog
        open={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setDelById(null);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ textAlign: "center", pt: 3 }}>
          <ErrorOutlineIcon sx={{ fontSize: 60, color: "#ef4444" }} />
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#111827", mt: 1 }}>
            Delete this report?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ textAlign: "center", fontSize: 13 }}>
            The reported issue and all its attachments will be permanently removed.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, justifyContent: "center", gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setDeleteOpen(false);
              setDelById(null);
            }}
            sx={{ textTransform: "none", borderRadius: 1.5, fontSize: 12 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDelete}
            sx={{
              textTransform: "none",
              borderRadius: 1.5,
              fontSize: 12,
              bgcolor: "#ef4444",
              "&:hover": { bgcolor: "#dc2626" },
            }}
          >
            Yes, Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Confirm Resolve ─── */}
      <Dialog
        open={resolveOpen}
        onClose={() => {
          if (!resolving) {
            setResolveOpen(false);
            setResolveDoc(null);
          }
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ textAlign: "center", pt: 3 }}>
          <CheckCircle sx={{ fontSize: 60, color: "#22c55e" }} />
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#111827", mt: 1 }}>
            Mark as resolved?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ textAlign: "center", fontSize: 13 }}>
            This will move the report to the Resolved tab. You can always view it there.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, justifyContent: "center", gap: 1 }}>
          <Button
            variant="outlined"
            disabled={resolving}
            onClick={() => {
              setResolveOpen(false);
              setResolveDoc(null);
            }}
            sx={{ textTransform: "none", borderRadius: 1.5, fontSize: 12 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleResolve}
            disabled={resolving}
            startIcon={resolving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null}
            sx={{
              textTransform: "none",
              borderRadius: 1.5,
              fontSize: 12,
              bgcolor: "#22c55e",
              "&:hover": { bgcolor: "#16a34a" },
            }}
          >
            {resolving ? "Resolving..." : "Yes, Resolve"}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default withTranslation()(IssueReportPanel);

IssueReportPanel.propTypes = {
  t: PropTypes.any,
};